import { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";

export default function Home() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [recentPredictions, setRecentPredictions] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const userSnap = await getDoc(doc(db, "users", currentUser.uid));
      if (userSnap.exists()) setUserData(userSnap.data());
      const predsSnap = await getDocs(
        query(collection(db, "predictions"), where("userId", "==", currentUser.uid))
      );
      const preds = predsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecentPredictions(preds.slice(0, 5));
      setTotalPoints(preds.reduce((sum, p) => sum + (p.pointsEarned || 0), 0));
      setLoading(false);
    }
    fetchData();
  }, [currentUser]);

  const displayName = userData?.displayName || currentUser.email.split("@")[0];
  const predCount = recentPredictions.length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />

      {/* Hero banner */}
      <div style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid #E0E2DB" }}>
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div className="fade-up">
              <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">Welcome back</p>
              <h1 className="font-display text-7xl tracking-wider leading-none" style={{ color: "#0A0B0F" }}>
                {displayName.toUpperCase()}
              </h1>
            </div>
            <div className="flex gap-4 fade-up fade-up-delay-1">
              <Link to="/events" className="btn-lime px-8 py-3 rounded-xl">
                MAKE PREDICTIONS
              </Link>
              <Link
                to="/leagues"
                className="font-condensed text-lg tracking-wide px-8 py-3 rounded-xl transition"
                style={{ border: "1px solid #E0E2DB", color: "#666" }}
              >
                MY LEAGUES
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 fade-up fade-up-delay-1">
          {[
            { label: "Total Points", value: totalPoints, accent: true },
            { label: "Predictions Made", value: predCount, accent: false },
            { label: "Correct Winners", value: recentPredictions.filter(p => p.pointsEarned >= 2).length, accent: false },
            { label: "Exact Scores", value: recentPredictions.filter(p => p.pointsEarned >= 5).length, accent: false },
          ].map((stat, i) => (
            <div key={i} className="card p-6" style={stat.accent ? { borderColor: "#C8FF00", borderWidth: "2px" } : {}}>
              <div className="font-display text-5xl mb-1" style={{ color: stat.accent ? "#0A0B0F" : "#0A0B0F" }}>
                {stat.accent && <span className="inline-block px-2 py-0 rounded mr-1" style={{ backgroundColor: "#C8FF00" }}>
                  {loading ? "—" : stat.value}
                </span>}
                {!stat.accent && (loading ? "—" : stat.value)}
              </div>
              <div className="font-condensed text-gray-500 tracking-wide uppercase text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent predictions */}
          <div className="card p-6 fade-up fade-up-delay-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>MY RECENT PICKS</h2>
              <Link to="/my-predictions" className="font-condensed text-sm tracking-wide hover:underline" style={{ color: "#C8FF00", filter: "brightness(0.7)" }}>
                VIEW ALL →
              </Link>
            </div>
            {loading ? (
              <p className="font-condensed text-gray-400">Loading...</p>
            ) : recentPredictions.length === 0 ? (
              <div className="text-center py-8">
                <p className="font-condensed text-gray-400 text-lg mb-4">No predictions yet</p>
                <Link to="/events" className="btn-lime px-6 py-2 rounded-lg text-base">
                  MAKE YOUR FIRST PICK
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentPredictions.map((pred) => (
                  <div key={pred.id} className="flex justify-between items-center py-3"
                    style={{ borderBottom: "1px solid #E0E2DB" }}>
                    <div>
                      <div className="font-condensed tracking-wide" style={{ color: "#0A0B0F" }}>
                        {pred.winner} to win
                      </div>
                      <div className="font-condensed text-gray-400 text-sm">
                        {pred.homeScore} – {pred.awayScore}
                        {pred.goalscorer && ` · ${pred.goalscorer}`}
                      </div>
                    </div>
                    <div className="font-display text-2xl" style={{ color: pred.pointsEarned > 0 ? "#5A8A00" : "#CCC" }}>
                      {pred.pointsEarned > 0 ? `+${pred.pointsEarned}` : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="space-y-4 fade-up fade-up-delay-3">
            <Link to="/events" className="card p-6 flex items-center justify-between transition-all hover:shadow-md block"
              style={{ borderLeft: "4px solid #C8FF00" }}>
              <div>
                <div className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>UPCOMING MATCHES</div>
                <div className="font-condensed text-gray-400 tracking-wide">Make your predictions before kickoff</div>
              </div>
              <div className="font-display text-4xl" style={{ color: "#C8FF00" }}>→</div>
            </Link>

            <Link to="/leagues" className="card p-6 flex items-center justify-between transition-all hover:shadow-md block"
              style={{ borderLeft: "4px solid #FF5C00" }}>
              <div>
                <div className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>MY LEAGUES</div>
                <div className="font-condensed text-gray-400 tracking-wide">Check leaderboards and standings</div>
              </div>
              <div className="font-display text-4xl" style={{ color: "#FF5C00" }}>→</div>
            </Link>

            <Link to="/my-predictions" className="card p-6 flex items-center justify-between transition-all hover:shadow-md block"
              style={{ borderLeft: "4px solid #7C3AED" }}>
              <div>
                <div className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>PREDICTION HISTORY</div>
                <div className="font-condensed text-gray-400 tracking-wide">Review and edit your picks</div>
              </div>
              <div className="font-display text-4xl text-purple-500">→</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}